<template>
  <AppNotification border-color="border-border-information">
    <template #header>
      <nuxt-icon
        filled
        name="alert-circle-blue"
        class="text-[2.4rem]"
        aria-hidden="true"
      />
      <p class="heading-xxs" aria-hidden="true">
        Ett SMS är skickat till
        {{ numberConfig.keepNumberInputValue }}. Fyll i koden nedan för att
        behålla ditt nummer.
      </p>
    </template>
    <div class="flex flex-col gap-8" aria-live="polite" role="status">
      <span class="sr-only"
        >Ett SMS är skickat till {{ numberConfig.keepNumberInputValue }}. Fyll i
        koden nedan för att behålla ditt nummer.</span
      >
    </div>
  </AppNotification>

  <div
    v-if="isTestEnvironment() && numberConfig.keepNumberCodeSent"
    class="w-full px-16 py-16 bg-surface-warning"
  >
    <p class="heading-xxs pb-8">Endast för testmiljö</p>
    <button
      class="underline text-text-link body-link-md hover:text-text-link-hover"
      type="button"
      @click="setTestCode"
    >
      Fyll i verifieringskod
    </button>
  </div>

  <div>
    <form class="flex items-flex-start gap-8" @submit.prevent="validateCode">
      <InputText
        v-model="numberConfig.keepNumberCodeInputValue"
        class="full-width"
        :name="codeField"
        :input-id="codeField"
        type="text"
        help-text="Rätt format: 1234"
        label="Verifieringskod"
        placeholder="Ange verifieringskod"
        mode="passive"
        :validate="[
          'smsCodeNotEmpty',
          'smsCodeCorrectFormat',
          'phoneNumberToKeepHasBeenValidatedBeforeStep2Submit',
        ]"
        @is-valid="isInputValid = $event"
      />
      <div class="flex-shrink-0 mt-11">
        <AppButton
          :show-loading="isSubmitting"
          color="primary"
          aria-label="Godkänn nummerflytt"
          squared
        >
          <nuxt-icon name="arrow-right" filled aria-hidden="true" />
        </AppButton>
      </div>
    </form>
  </div>
  <p>
    Har du inte fått någon kod? Kontrollera att du skrivit in rätt
    telefonnummer. Om du inte fått någon kod inom 5 minuter,
    <NuxtLink to="https://www.halebop.se/kontakta-oss" external target="_blank">
      chatta med supporten
    </NuxtLink>
    så hjälper vi dig!
  </p>
</template>

<script lang="ts" setup>
import { AddValidatorKey } from '~/formAggregator';
import type { HalebopRequestErrorData } from '~/repository/factory';
import { isTestEnvironment } from '~/utils/general/isTestEnvironment';
const { $api } = useNuxtApp();
const props = defineProps<{ cartItemId: number; bundleId: number }>();

const isInputValid = ref(false);

const codeField = computed(() => {
  return `code_${props.cartItemId}_${props.bundleId}`;
});
const { numberConfig } = useSelectNumberConfig({
  cartItemBundleId: props.bundleId,
  cartItemId: props.cartItemId,
});

const { handleSubmit, validate, isSubmitting, setFieldValue } = useForm();

const setTestCode = () => {
  numberConfig.value.keepNumberCodeInputValue =
    numberConfig.value.keepNumberDevPortingCode;
  setFieldValue(codeField.value, numberConfig.value.keepNumberCodeInputValue);
};

// add this component's validator to the aggregator
const addValidator = inject(AddValidatorKey, () => {
  throw new Error('No aggregator provided for inject');
});
addValidator(validate);

const validateCode = handleSubmit(async (values, actions) => {
  // Reset validation state
  numberConfig.value.keepNumberCodeValidated = false;
  numberConfig.value.keepNumberShowError = false;

  try {
    const isValid = await $api.PortingModule.validatePortingCode(
      values[codeField.value],
      numberConfig.value.keepNumberInputValue,
    );
    if (!isValid) {
      actions.setFieldError(codeField.value, 'Koden är inte korrekt');
      numberConfig.value.keepNumberCodeValidated = false;
      numberConfig.value.keepNumberShowError = true;
      numberConfig.value.keepNumberCodeInputValue = '';
    } else {
      numberConfig.value.keepNumberCodeValidated = true;
      numberConfig.value.keepNumberShowError = false;
      setFieldValue(codeField.value, values[codeField.value]);
      await validate();
    }
  } catch (error) {
    if (isNuxtError<HalebopRequestErrorData>(error) && error.data) {
      actions.setFieldError(
        codeField.value,
        'Kontrollera att du har fyllt i koden korrekt',
      );
      return;
    }
  }
});
</script>

<style scoped lang="scss"></style>
